import countries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import et from "i18n-iso-countries/langs/et.json";
import lt from "i18n-iso-countries/langs/lt.json";
import lv from "i18n-iso-countries/langs/lv.json";
import pl from "i18n-iso-countries/langs/pl.json";
import ru from "i18n-iso-countries/langs/ru.json";
import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpApi, {
  type HttpBackendOptions,
  type RequestCallback,
} from "i18next-http-backend";
import { initReactI18next } from "react-i18next";
import {
  FlagDEVIcon,
  FlagEEIcon,
  FlagGBIcon,
  FlagLTIcon,
  FlagPLIcon,
  FlagRUIcon,
} from "@components/icons/flags";
import { useI18nStore } from "@store/i18n/i18nStore";
import { isNullOrUndefined } from "@utils/common";
import { type TFormatDateLocale } from "@utils/units";
import { assetsApi, translationsApi } from "@/api";
import {
  AVAILABLE_LANGUAGES,
  COUNTRY_CODE,
  IS_DEV,
  IS_TESTING_ENV,
} from "./constants";

countries.registerLocale(lt);
countries.registerLocale(en);
countries.registerLocale(ru);
countries.registerLocale(et);
countries.registerLocale(pl);
countries.registerLocale(lv);

export enum CountryCode {
  LT = "lt",
  EE = "ee",
  PL = "pl",
}

export enum Language {
  LT = "lt",
  EN = "en",
  RU = "ru",
  EE = "ee",
  PL = "pl",
  DEV = "dev",
}

export enum ApplicationId {
  LT_INTERNAL = 6,
  EE_INTERNAL = 8,
  PL_INTERNAL = 11,
  LV_INTERNAL = 13,
}

type TAvailableLanguage = Partial<
  Record<
    Language,
    {
      icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    }
  >
>;

type TLanguageMetadata = Record<
  CountryCode,
  {
    defaultLanguage: Language;
    availableLanguages: TAvailableLanguage;
    translationsApplicationId: ApplicationId;
  }
>;

const allAvailableLanguages: TAvailableLanguage = {
  [Language.LT]: {
    icon: FlagLTIcon,
  },
  [Language.EE]: {
    icon: FlagEEIcon,
  },
  [Language.RU]: {
    icon: FlagRUIcon,
  },
  [Language.PL]: {
    icon: FlagPLIcon,
  },
  [Language.EN]: {
    icon: FlagGBIcon,
  },
};

export const languageConfig: TLanguageMetadata = {
  [CountryCode.LT]: {
    defaultLanguage: Language.LT,
    availableLanguages: {},
    translationsApplicationId: ApplicationId.LT_INTERNAL,
  },
  [CountryCode.EE]: {
    defaultLanguage: Language.EE,
    availableLanguages: {},
    translationsApplicationId: ApplicationId.EE_INTERNAL,
  },
  [CountryCode.PL]: {
    defaultLanguage: Language.PL,
    availableLanguages: {},
    translationsApplicationId: ApplicationId.PL_INTERNAL,
  },
};

const getApplicationId = (): ApplicationId => {
  return COUNTRY_CODE === CountryCode.LT
    ? ApplicationId.LT_INTERNAL
    : COUNTRY_CODE === CountryCode.EE
      ? ApplicationId.EE_INTERNAL
      : COUNTRY_CODE === CountryCode.PL
        ? ApplicationId.PL_INTERNAL
        : ApplicationId.LV_INTERNAL;
};

const buildLanguageConfig = (): {
  defaultLanguage: Language;
  availableLanguages: TAvailableLanguage;
  translationsApplicationId: ApplicationId;
} => {
  const result = languageConfig[COUNTRY_CODE];

  const buildLanguages = AVAILABLE_LANGUAGES.split(",");

  if (buildLanguages.length > 0) {
    result.defaultLanguage = buildLanguages[0] as Language;

    buildLanguages.forEach((langKey: string) => {
      if (allAvailableLanguages[langKey as Language]) {
        result.availableLanguages[langKey as Language] =
          allAvailableLanguages[langKey as Language];
      }
    });
  }

  return result;
};

const getLanguageMetadata = () => {
  const result = buildLanguageConfig();

  //i18nextLng
  if (
    typeof window !== "undefined" &&
    isNullOrUndefined(localStorage.getItem("i18nextLng"))
  ) {
    localStorage.setItem("i18nextLng", result.defaultLanguage);
  }

  if (typeof window !== "undefined" && IS_TESTING_ENV) {
    result.availableLanguages.dev = { icon: FlagDEVIcon };
  }

  return result;
};

export const languageMetadata = getLanguageMetadata();

const loadLocalTranslations = async (
  language: string,
): Promise<Record<string, string>> => {
  return await assetsApi
    .get<Record<string, string>>(
      `/translations/${language}-${COUNTRY_CODE}.json`,
    )
    .then((response) => {
      return response.data;
    })
    .catch(() => {
      return {};
    });
};

const loadApiTranslations = async (
  language: string,
): Promise<Record<string, string>> => {
  return await translationsApi
    .get<Record<string, string>>(`/${language}/dictionary`, {
      params: { applicationId: getApplicationId() },
    })
    .then((response) => {
      return response.data;
    })
    .catch(async () => await loadLocalTranslations(language));
};

const backendOptions: HttpBackendOptions = {
  loadPath: "{{lng}}",
  //addPath: "", // will be passed as url in missing translations request
  request: (
    _options: HttpBackendOptions,
    language: string,
    _payload: Record<string, string>, // if it is not null - it is add missing translations request
    callback: RequestCallback,
  ) => {
    (IS_DEV ? loadLocalTranslations : loadApiTranslations)(language)
      .then((data) => {
        callback(null, {
          data: data,
          status: 200,
        });
      })
      .catch((e) => {
        console.error(e);

        callback(null, {
          data: {},
          status: 500,
        });
      });
  },
};

const i18n = i18next
  .use(HttpApi)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: Object.keys(languageMetadata.availableLanguages),
    backend: backendOptions,
    fallbackLng: IS_TESTING_ENV ? false : languageMetadata.defaultLanguage,
    interpolation: {
      escapeValue: false,
    },
    //saveMissing: true, -- if enabled - sents request for missing translations
  });

export default i18n;

i18next.on("languageChanged", (lng) => {
  useI18nStore.getState().setLocale(lng as TFormatDateLocale);
});
